'use client'

import { motion, useScroll, useTransform } from 'framer-motion'
import { useRef } from 'react'
import { MagneticLink } from './magnetic-button'

export function HeroSection() {
  const containerRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  })

  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])

  return (
    <section
      ref={containerRef}
      className="relative isolate z-0 h-[100dvh] min-h-[32rem] w-full shrink-0 overflow-hidden bg-background"
    >
      <motion.div className="pointer-events-none absolute inset-0">
        <div className="relative mx-auto h-full max-w-7xl px-6">
          <div className="absolute bottom-0 left-6 top-0 w-px bg-border/30" />
          <div className="absolute bottom-0 right-6 top-0 hidden w-px bg-border/30 lg:block" />
          <div className="absolute bottom-0 left-1/2 top-0 hidden w-px bg-border/30 lg:block" />
        </div>
      </motion.div>

      <motion.div
        className="absolute inset-x-0 bottom-0 top-0 z-10 flex items-start overflow-y-auto px-6 pt-24 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:bottom-[5.5rem] lg:items-center lg:pt-20"
        style={{ opacity }}
      >
        <div className="mx-auto w-full max-w-7xl py-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="mb-10 lg:mb-12"
          >
            <motion.div className="mb-8 flex items-center gap-4">
              <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
                Digital product studio
              </span>
            </motion.div>
          </motion.div>

          <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-8">
              <motion.h1
                className="mb-8 font-serif text-4xl font-normal leading-[0.9] tracking-tight md:text-6xl lg:text-7xl xl:text-8xl"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              >
                We design and build
                <br />
                <span className="italic text-accent">sites & software</span>
                <br />
                that ship.
              </motion.h1>

              <motion.div
                className="mt-10 flex flex-col gap-4 sm:flex-row"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <MagneticLink href="/portfolio" variant="primary" className="w-full sm:w-auto">
                  View Our Work
                </MagneticLink>
                <MagneticLink href="/contact" variant="outline" className="w-full sm:w-auto">
                  Start a Project
                </MagneticLink>
              </motion.div>
            </div>

            <motion.div
              className="lg:col-span-4 lg:border-l lg:border-border lg:pl-8"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <p className="mb-8 font-serif text-lg italic leading-relaxed text-muted-foreground lg:text-xl">
                Strategy, brand, UX, and engineering in one engagement - websites, POS, booking,
                CRM, and internal tools for founder-led teams.
              </p>

              <div className="space-y-6 border-t border-border pt-8">
                <div>
                  <span className="mb-2 block text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    What we build
                  </span>
                  <p className="text-sm">
                    Marketing sites, e-commerce, custom business software, and product launches
                  </p>
                </div>
                <div>
                  <span className="mb-2 block text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Who we work with
                  </span>
                  <p className="text-sm">Local businesses and founder-led teams across Canada</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="absolute bottom-0 left-0 right-0 z-20 hidden border-t border-border bg-background md:block"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-6 py-6 md:flex-row md:items-center">
          <div className="flex flex-wrap items-center gap-6 gap-y-2 md:gap-8">
            <a
              href="tel:+16393164005"
              className="inline-block py-3 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-accent"
            >
              (639) 316-4005
            </a>
            <a
              href="mailto:contact@elementarycreative.com"
              className="inline-block py-3 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-accent"
            >
              contact@elementarycreative.com
            </a>
          </div>
          <motion.div
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground"
            animate={{ y: [0, 5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            Scroll to discover
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M19 12l-7 7-7-7" />
            </svg>
          </motion.div>
        </div>
      </motion.div>
    </section>
  )
}
